import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommunitiesType } from 'src/app/macro-community/interfaces/building.interface';
import { NavigationUser } from '../interface/navigation.interface';
import { UserRoles } from '../interface/users.interface';
import { CUSTOMIZED_ROUTE_MAPPING, DefaultRoutes, ROLE_ROUTE_CONFIG } from '../shared/constants/routes.constants';
import { PROPRIETOR_DEFAULT_ROUTE, isProprietorAllowedUrl } from '../shared/constants/proprietor-access.constants';
import { isCommitteeAllowedUrl } from '../shared/constants/committee-access.constants';
import { canShowMunilyPayTransactionsMenu } from '../shared/constants/munily-pay-menu-access.constants';
import { WhiteLabelService } from './white-label.service';
import { ROUTES, mainFeatures } from '../shared/sidebar/menu-items';
import {
  ADMIN_CONDITIONAL_MENU_GATES,
  MENU_PATHS,
  isMenuKeyInWhiteLabelShowFeatures,
  isSuperAdminGuardedPath,
  resolveMenuKeyForPath,
} from '../shared/sidebar/menu-availability';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(
    private router: Router,
    private whiteLabelService: WhiteLabelService,
  ) {}

  /**
   * Determina la ruta destino post-login para el usuario.
   * Jerarquía de prioridad:
   *   1. whiteLabel.initialRoute (desde config del edificio)
   *   2. superUser
   *   3. committee (sin admin/superAdmin; gana sobre propietario exclusivo)
   *   4. proprietor exclusivo (sin admin/superAdmin ni committee)
   *   5. customized → ruta basada en features
   *   6. MacroCommunity admin
   *   7. Admin estándar
   *   8. Fallback
   */
  getTargetRouteForUser(user: NavigationUser): string {
    const building = user.buildings?.[0];
    const roles = user.roles || [];

    // 1. Verificar si hay initialRoute en la config de white label (PRIORIDAD MÁXIMA)
    const initialRoute = this.whiteLabelService.getInitialRoute(building);
    if (initialRoute) {
      return initialRoute.startsWith('/') ? initialRoute : `/${initialRoute}`;
    }

    // 2. superUser
    if (user.superUser) {
      return DefaultRoutes.SUPER_ADMIN_DASHBOARD;
    }

    const isProprietor = roles.includes(UserRoles.proprietor as string);
    const isAdmin = roles.includes(UserRoles.admin as string);
    const isSuperAdmin = roles.includes(UserRoles.superAdmin as string);
    const isCustomized = roles.includes(UserRoles.customized as string);
    const isCommittee = roles.includes(UserRoles.committee as string);

    // 3. committee (sin admin/superAdmin)
    if (isCommittee && !isAdmin && !isSuperAdmin) {
      return ROLE_ROUTE_CONFIG.committee.traditional;
    }

    // 4. proprietor exclusivo (sin admin/superAdmin ni committee)
    if (isProprietor && !isAdmin && !isSuperAdmin && !isCommittee) {
      return PROPRIETOR_DEFAULT_ROUTE;
    }

    // 5. customized → ruta basada en features
    if (isCustomized) {
      return this.getCustomizedRoute(user);
    }

    // 6. MacroCommunity
    if (this.isMacroFather(user)) {
      return DefaultRoutes.MACRO_COMMUNITY_HOME;
    }

    // 7. Admin estándar
    if (isAdmin) {
      return ROLE_ROUTE_CONFIG.admin.traditional;
    }

    if (isSuperAdmin) {
      return ROLE_ROUTE_CONFIG.superAdmin.traditional;
    }

    // 8. Fallback
    return DefaultRoutes.ADMIN_DASHBOARD;
  }

  private getCustomizedRoute(user: NavigationUser): string {
    const features = user.features?.map((f) => f.feature[0]).filter(Boolean) || [];
    const isMacro = this.isMacroFather(user);

    if (isMacro) {
      return this.getMacroCommunityRoute(user);
    }

    for (const feature of features) {
      if (CUSTOMIZED_ROUTE_MAPPING[feature as keyof typeof CUSTOMIZED_ROUTE_MAPPING]) {
        return CUSTOMIZED_ROUTE_MAPPING[feature as keyof typeof CUSTOMIZED_ROUTE_MAPPING];
      }
    }

    return ROLE_ROUTE_CONFIG.customized.traditional;
  }

  private getMacroCommunityRoute(user: NavigationUser): string {
    const features = user.features?.map((feature) => feature.feature[0]).filter(Boolean) || [];

    for (const feature of features) {
      if (CUSTOMIZED_ROUTE_MAPPING[feature as keyof typeof CUSTOMIZED_ROUTE_MAPPING]) {
        return CUSTOMIZED_ROUTE_MAPPING[feature as keyof typeof CUSTOMIZED_ROUTE_MAPPING];
      }
    }

    return DefaultRoutes.MACRO_COMMUNITY_HOME;
  }

  private isMacroFather(user: NavigationUser): boolean {
    const macro = user.buildings?.[0]?.config?.macrocommunity;
    return !!(macro?.enable && macro?.communities?.[0]?.type === CommunitiesType.macrocommunity);
  }

  navigateToUserRoute(user: NavigationUser): void {
    const targetRoute = this.getTargetRouteForUser(user);
    this.router.navigate([targetRoute]);
  }

  /**
   * VDS-3315 — resolves the reload target for a multisession account switch: the
   * current module's path preserved for `newUser`, if that module is admissible for
   * them, otherwise `getTargetRouteForUser(newUser)`.
   *
   * MUST NOT call `dataService.getUser()` / `getBuilding()` — `newUser` is the only
   * source of truth here; `getBuilding()` reads `localStorage.currentUser`, which is
   * exactly what `dataService.setUser()` is about to rewrite (design.md D5).
   */
  resolvePostSwitchRoute(currentUrl: string, newUser: NavigationUser): string {
    const fallback = this.getTargetRouteForUser(newUser);
    const path = currentUrl?.split('#')[0]?.split('?')[0];

    if (!path || path === '/') {
      return fallback;
    }

    if (isSuperAdminGuardedPath(path)) {
      if (!this.hasSuperAdminAccess(newUser)) {
        return fallback;
      }
      // D3 extra guard: /app/munily-pay/** also requires SuperAdminMunilyPayRol's email
      // allow-list. Reusing canShowMunilyPayTransactionsMenu prevents stranding a superUser
      // outside the allow-list on a route whose guard has no redirect (blank shell).
      if (this.isMunilyPayGuardedPath(path) && !canShowMunilyPayTransactionsMenu(newUser)) {
        return fallback;
      }
    }

    const key = resolveMenuKeyForPath(path);
    if (!key) {
      return fallback;
    }

    const modulePath = MENU_PATHS[key];
    if (!this.isMenuKeyAvailableForUser(key, modulePath, newUser)) {
      return fallback;
    }

    return modulePath;
  }

  /** Guard axis of D3 — keyed on the `superAdmin` role, not `superUser`. */
  hasSuperAdminAccess(user: NavigationUser): boolean {
    return (user.roles || []).includes(UserRoles.superAdmin as string);
  }

  private isMunilyPayGuardedPath(path: string): boolean {
    const prefix = '/app/munily-pay';
    return path === prefix || path.startsWith(`${prefix}/`);
  }

  /**
   * Menu axis of D1 — mirrors `buildMenu()`'s dispatch order exactly (branches a-f of
   * design.md's Resolution decision table). Fail-open: an unmapped gate defaults to
   * available via the `?? true` on branch (f).
   */
  isMenuKeyAvailableForUser(key: string, modulePath: string, user: NavigationUser): boolean {
    const building = user.buildings?.[0];
    const roles = user.roles || [];
    const isProprietor = roles.includes(UserRoles.proprietor as string);
    const isAdmin = roles.includes(UserRoles.admin as string);
    const isSuperAdmin = roles.includes(UserRoles.superAdmin as string);
    const isCustomized = roles.includes(UserRoles.customized as string);
    const isCommittee = roles.includes(UserRoles.committee as string);

    // a. superUser — guard axis already applied by the caller.
    if (user.superUser) {
      return true;
    }

    // b. proprietor exclusivo.
    if (isProprietor && !isAdmin) {
      return isProprietorAllowedUrl(modulePath);
    }

    // c. customized.
    if (isCustomized) {
      return this.hasAccessToRoute(user, modulePath);
    }

    // d. white-label showFeatures.
    const showFeatures = this.whiteLabelService.getMenuShowFeatures(building);
    if (showFeatures?.length) {
      return isMenuKeyInWhiteLabelShowFeatures(key, showFeatures);
    }

    // e. committee (sin admin/superAdmin, para permanecer fail-open con Roles/getTargetRouteForUser).
    if (isCommittee && !isAdmin && !isSuperAdmin) {
      return isCommitteeAllowedUrl(modulePath);
    }

    // f. standard admin (traditional or macro) — cross-tree /app <-> /app-community check (5b).
    const isMacroFatherAccount = this.isMacroFather(user);
    const isCommunityTreePath = modulePath.startsWith('/app-community');
    if (isMacroFatherAccount !== isCommunityTreePath) {
      return false;
    }

    // Gates mirror `buildTraditionalAdminMenu(buildingCofig)`, which receives `building.config`.
    return ADMIN_CONDITIONAL_MENU_GATES[key]?.(building?.config, user) ?? true;
  }

  /**
   * Verifica si un usuario customized tiene acceso a la ruta solicitada.
   * Se construye la lista de rutas permitidas a partir de sus features habilitados,
   * igual que lo hace el sidebar con buildCustomizedMenu.
   */
  hasAccessToRoute(user: NavigationUser, route: string): boolean {
    const roles = user.roles || [];
    const isCustomized = roles.includes(UserRoles.customized as string);

    if (!isCustomized) {
      return true;
    }

    // Para usuarios customized sin white label específico, verificar features
    const allowedPaths = this.getCustomizedAllowedPaths(user);
    const normalized = route.split('?')[0];
    return allowedPaths.some((path) => normalized === path || normalized.startsWith(path + '/'));
  }

  private getCustomizedAllowedPaths(user: NavigationUser): string[] {
    const paths: string[] = [];
    const features = user.features || [];

    for (const feat of features) {
      if (!feat.feature) continue;
      for (const userFeature of feat.feature) {
        const routeInfo = ROUTES[userFeature];
        if (routeInfo?.path) {
          paths.push(routeInfo.path);
        }
        if (routeInfo?.submenu) {
          for (const sub of routeInfo.submenu) {
            if (sub?.path) paths.push(sub.path);
          }
        }
      }
    }

    const targetRoute = this.getTargetRouteForUser(user);
    if (targetRoute && !paths.includes(targetRoute)) {
      paths.push(targetRoute);
    }
    this.appendTicketsV2PathWhenPqrsAllowed(paths);

    return [...new Set(paths)];
  }

  /** Customized users with PQRS must also reach Tickets v2 (same scope as sidebar). */
  private appendTicketsV2PathWhenPqrsAllowed(paths: string[]): void {
    const pqrsPath = ROUTES[mainFeatures.pqrs]?.path;
    const ticketsPath = ROUTES[mainFeatures.ticketsV2]?.path;
    if (pqrsPath && ticketsPath && paths.includes(pqrsPath) && !paths.includes(ticketsPath)) {
      paths.push(ticketsPath);
    }
  }

  isRouteInGroup(route: string, routeGroup: readonly DefaultRoutes[]): boolean {
    return routeGroup.includes(route as DefaultRoutes);
  }
}
